import { apiRequest } from './api'
import { VaBalanceLog } from './users.service'

export interface FinanceMember {
  id: string
  memberId: string
  firstName: string
  lastName: string
  email: string
  role: string
  isActive: boolean
  virtualAccountNumber: string | null
  virtualAccountBank: string | null
  /** Informational VA balance (RM) — null = never set. */
  virtualAccountBalance: number | null
}

export interface FinanceMemberDetail extends FinanceMember {
  walletNumber: string | null
  vaBalanceLogs: VaBalanceLog[]
}

export interface FinanceMemberListResponse {
  data: FinanceMember[]
  meta: { total: number; count: number; limit: number }
}

export interface VaBalanceLogsResponse {
  data: VaBalanceLog[]
  meta: { total: number; count: number; limit: number }
}

export interface VaBalanceBulkFailedRow {
  row: number
  vaNumber: string | null
  reasons: string[]
}

export interface VaBalanceBulkResult {
  totalRows: number
  /** Rows whose balance actually changed (audited). */
  updated: number
  /** Rows that already had this balance (no audit entry). */
  unchanged: number
  failed: VaBalanceBulkFailedRow[]
}

/** Finance staff surface: member search + informational VA balance only. */
export const financeService = {
  async searchMembers(search: string, limit = 20): Promise<FinanceMemberListResponse> {
    const response = await apiRequest.get<FinanceMemberListResponse>('/finance/members', {
      search: search || undefined,
      limit,
    })
    return response.data
  },

  async getMember(id: string): Promise<FinanceMemberDetail> {
    const response = await apiRequest.get<FinanceMemberDetail>(`/finance/members/${id}`)
    return response.data
  },

  async setVaBalance(id: string, balance: number, note?: string): Promise<{ unchanged: boolean }> {
    const response = await apiRequest.patch<{ unchanged: boolean }>(
      `/finance/members/${id}/va-balance`,
      { balance, note },
    )
    return response.data
  },

  async getVaBalanceLogs(id: string, limit = 50): Promise<VaBalanceLogsResponse> {
    const response = await apiRequest.get<VaBalanceLogsResponse>(
      `/finance/members/${id}/va-balance/logs`,
      { limit },
    )
    return response.data
  },

  async bulkUploadVaBalances(formData: FormData): Promise<VaBalanceBulkResult> {
    const response = await apiRequest.upload<VaBalanceBulkResult>(
      '/finance/va-balances/bulk-upload',
      formData,
    )
    return response.data
  },
}
